import React, { useEffect, useMemo, useRef, useState } from "react";

const Header = ({ navigation, content }) => {
  const [slideIndex, setSlideIndex] = useState(1);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const dragStart = useRef(0);
  const quotes = useMemo(() => content?.quotes || [], [content]);
  const mobileQuotes = useMemo(
    () => quotes.length ? [quotes[quotes.length - 1], ...quotes, quotes[0]] : [],
    [quotes]
  );

  useEffect(() => {
    if (quotes.length < 2 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const timer = window.setTimeout(() => setSlideIndex((current) => current + 1), 7000);
    return () => window.clearTimeout(timer);
  }, [quotes.length, slideIndex]);

  const finishLoop = () => {
    if (slideIndex !== 0 && slideIndex !== quotes.length + 1) return;
    setIsResetting(true);
    setSlideIndex(slideIndex === 0 ? quotes.length : 1);
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => setIsResetting(false)));
  };

  const showPreviousQuote = () => setSlideIndex((current) => current - 1);
  const showNextQuote = () => setSlideIndex((current) => current + 1);

  const beginSwipe = (event) => {
    dragStart.current = event.clientX;
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveSwipe = (event) => {
    if (!isDragging) return;
    setDragOffset(event.clientX - dragStart.current);
  };

  const endSwipe = (event) => {
    if (!isDragging) return;
    const distance = event.clientX - dragStart.current;
    const threshold = Math.min(64, event.currentTarget.clientWidth * 0.16);
    if (Math.abs(distance) >= threshold) {
      setSlideIndex((current) => current + (distance < 0 ? 1 : -1));
    }
    setDragOffset(0);
    setIsDragging(false);
  };

  if (!navigation || !content) return null;

  return (
    <header id="home" className="hero">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <nav className="site-nav" aria-label={navigation.ariaLabel}>
        <a className="wordmark" href="#home" aria-label={navigation.homeLabel}>
          <img src="images/header-logo.png" alt="" />
        </a>
        <div className="nav-links">
          <a href="#product">{navigation.productsLabel}</a>
          <a href="#interview">{navigation.interviewLabel}</a>
        </div>
      </nav>
      <div className="hero-image" style={{ backgroundImage: `url(images/${content.backgroundImageFilename})` }} aria-hidden="true" />
      <div className="hero-shade" aria-hidden="true" />
      <div className="hero-content">
        <p className="hero-label">Independent designer · Hong Kong</p>
        <h1>{content.heading}</h1>
      </div>
      <div className="hero-quotes" aria-label="Selected quotes">
        <div className="quote-track-desktop">
          {quotes.map((quote) => (
            <blockquote key={quote.author}>
              <p>{quote.text}</p>
              <cite>{quote.author}</cite>
            </blockquote>
          ))}
        </div>
        <div className="quote-carousel" aria-label="Swipe to view quotes" aria-live="off">
          <button className="quote-arrow quote-arrow-left" type="button" aria-label="Previous quote" aria-controls="quote-track" onClick={showPreviousQuote}>←</button>
          <div
            id="quote-track"
            className={`quote-track${isDragging ? " is-dragging" : ""}${isResetting ? " is-resetting" : ""}`}
            style={{ transform: `translateX(calc(-${slideIndex * 100}% + ${dragOffset}px))` }}
            onPointerDown={beginSwipe}
            onPointerMove={moveSwipe}
            onPointerUp={endSwipe}
            onPointerCancel={endSwipe}
            onTransitionEnd={finishLoop}
          >
            {mobileQuotes.map((quote, index) => (
              <blockquote aria-hidden={index !== slideIndex} key={`${quote.author}-${index}`}>
                <p>{quote.text}</p>
                <cite>{quote.author}</cite>
              </blockquote>
            ))}
          </div>
          <button className="quote-arrow quote-arrow-right" type="button" aria-label="Next quote" aria-controls="quote-track" onClick={showNextQuote}>→</button>
        </div>
      </div>
    </header>
  );
};

export default Header;
